<template>
  <v-dialog max-width="600px" persistent v-model="dialog">
    <v-card class="edit-customer-card" v-if="selectedCustomer != null">
      <!-- Modern Header -->
      <div class="edit-header">
        <div class="header-icon">
          <v-icon color="#fddf00" size="32">edit</v-icon>
        </div>
        <div class="header-content">
          <h2 class="header-title">Edit Customer</h2>
          <p class="header-subtitle">Update customer information</p>
        </div>

        <v-btn icon @click="$emit('setDialog', false)" class="close-btn">
          <v-icon>close</v-icon>
        </v-btn>
      </div>

      <v-divider></v-divider>

      <!-- Form Content -->
      <v-card-text class="edit-content">
        <!-- Customer Info Display -->
        <div class="customer-summary">
          <div class="customer-avatar">
            <v-icon color="#fddf00" size="28">account_circle</v-icon>
          </div>
          <div class="customer-basic-info">
            <h3 class="customer-display-name">
              {{ selectedCustomer.user_first_name }}
              {{ selectedCustomer.user_last_name }}
            </h3>
            <p class="customer-id">ID: {{ selectedCustomer.user_id }}</p>
            <div class="customer-stats">
              <v-chip color="grey lighten-3" small class="stat-chip">
                <v-icon left size="12">stars</v-icon>
                {{ selectedCustomer.user_sum_point }} pts
              </v-chip>
              <v-chip
                :color="getTypeColor(selectedCustomer.user_type)"
                small
                class="stat-chip"
                :class="{ 'facebook-chip': selectedCustomer.user_type == '2' }"
              >
                {{ getUserType(selectedCustomer.user_type) }}
              </v-chip>
              <v-chip
                :color="
                  selectedCustomer.user_vip ? '#fddf00' : 'grey lighten-3'
                "
                small
                class="stat-chip"
              >
                <v-icon left size="12">
                  {{ selectedCustomer.user_vip ? "star" : "star_border" }}
                </v-icon>
                {{ selectedCustomer.user_vip ? "VIP" : "Standard" }}
              </v-chip>
            </div>
          </div>
        </div>

        <!-- Edit Form - Only Editable Fields -->
        <v-form ref="form" v-model="valid" lazy-validation class="edit-form">
          <!-- Personal Information Section -->
          <div class="form-section">
            <div class="section-header">
              <h4 class="section-title">
                <v-icon color="#fddf00" left>person</v-icon>
                Personal Information
              </h4>
            </div>

            <div class="form-grid">
              <v-text-field
                v-model="selectedCustomer.user_first_name"
                label="First Name"
                outlined
                color="#fddf00"
                :rules="[nameValid.max, nameValid.required]"
                class="form-input"
              >
                <template v-slot:prepend-inner>
                  <v-icon color="#fddf00" size="20">badge</v-icon>
                </template>
              </v-text-field>

              <v-text-field
                v-model="selectedCustomer.user_last_name"
                label="Last Name"
                outlined
                color="#fddf00"
                :rules="[nameValid.max, nameValid.required]"
                class="form-input"
              >
                <template v-slot:prepend-inner>
                  <v-icon color="#fddf00" size="20">badge</v-icon>
                </template>
              </v-text-field>
            </div>

            <v-text-field
              v-model="selectedCustomer.user_email"
              label="Email Address"
              outlined
              color="#fddf00"
              :rules="[
                emailValid.email,
                emailValid.required,
                emailValid.already,
              ]"
              class="form-input"
            >
              <template v-slot:prepend-inner>
                <v-icon color="#fddf00" size="20">email</v-icon>
              </template>
            </v-text-field>
          </div>

          <!-- Contact Information Section -->
          <div class="form-section">
            <div class="section-header">
              <h4 class="section-title">
                <v-icon color="#fddf00" left>phone</v-icon>
                Contact Information
              </h4>
            </div>

            <div class="phone-group">
              <v-select
                :items="country_tel"
                v-model="selectedCustomer.user_country_tel"
                label="Country Code"
                outlined
                color="#fddf00"
                class="country-select"
              >
                <template v-slot:prepend-inner>
                  <v-icon color="#fddf00" size="20">public</v-icon>
                </template>
              </v-select>

              <v-text-field
                v-model="selectedCustomer.user_tel"
                label="Phone Number"
                outlined
                color="#fddf00"
                :rules="[telValid.leng, telValid.already, telValid.required]"
                class="phone-input"
              >
                <template v-slot:prepend-inner>
                  <v-icon color="#fddf00" size="20">phone</v-icon>
                </template>
              </v-text-field>
            </div>
          </div>
        </v-form>

        <!-- Action Notice -->
        <div class="action-notice">
          <v-icon color="info" size="20">info</v-icon>
          <div class="notice-content">
            <p class="notice-title">Update Information</p>
            <p class="notice-text">Changes will be saved immediately.</p>
          </div>
        </div>
      </v-card-text>

      <!-- Action Buttons -->
      <v-card-actions class="edit-actions">
        <v-btn
          color="blue darken-1"
          text
          @click="openPasswordReset()"
          class="password-btn"
        >
          <v-icon left color="white">lock_reset</v-icon>
          Reset Password
        </v-btn>

        <v-spacer></v-spacer>

        <v-btn text color="grey" @click="cancel()" class="cancel-btn">
          Cancel
        </v-btn>

        <v-btn
          color="#fddf00"
          @click="save()"
          :loading="isSaving"
          :disabled="!valid"
          class="save-btn"
        >
          <v-icon left>save</v-icon>
          Save Changes
        </v-btn>
      </v-card-actions>
    </v-card>

    <!-- Password Reset Dialog -->
    <ResetPasswordForm
      :dialog="passwordResetDialog"
      :customer="selectedCustomer"
      @setDialog="passwordResetDialog = $event"
      @password-reset-success="handlePasswordResetSuccess"
      @password-reset-error="handlePasswordResetError"
    />
  </v-dialog>
</template>

<script>
import { mapState } from "vuex";
import ResetPasswordForm from "./ResetPasswordForm.vue";
import moment from "moment";

export default {
  components: {
    ResetPasswordForm,
  },
  props: ["dialog", "selectedCustomer", "original"],
  data() {
    return {
      country_tel: ["+66"],
      passwordResetDialog: false,
      valid: true,
      isSaving: false,
      telValid: {
        required: (value) => !!value || "Phone number is required",
        leng: (v) => v.length == 9 || "Phone number must be 9 digits",
        already: (a) => !this.telAlready(a) || "Phone number already in use",
      },
      nameValid: {
        required: (value) => !!value || "Name is required",
        max: (v) => v.length <= 55 || "Maximum 55 characters allowed",
      },
      emailValid: {
        already: (a) => !this.emailAlready(a) || "Email already in use",
        required: (value) => !!value || "Email is required",
        email: (value) => {
          const pattern =
            /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
          return pattern.test(value) || "Invalid email address";
        },
      },
      // User type mapping
      userTypeMap: {
        1: "Weserve",
        2: "Facebook",
        default: "Other",
      },
      // Color mappings
      typeColorMap: {
        1: "#fddf00",
        2: "#4267B2",
        default: "grey lighten-2",
      },
    };
  },
  computed: {
    ...mapState({
      customerData: (state) => state.fetchCustomerBack.customerData,
    }),
  },
  methods: {
    telAlready(a) {
      let check = 0;
      let ID2 = this.selectedCustomer.user_id;
      for (let key in this.customerData) {
        if (this.customerData[key].user_id != ID2) {
          if (a == this.customerData[key].user_tel) {
            check = 1;
          }
        }
      }
      return check === 1;
    },
    emailAlready(a) {
      let check = 0;
      let ID1 = this.selectedCustomer.user_id;
      for (let key in this.customerData) {
        if (this.customerData[key].user_id != ID1) {
          if (a == this.customerData[key].user_email) {
            check = 1;
          }
        }
      }
      return check === 1;
    },
    getTypeColor(userType) {
      return this.typeColorMap[userType] || this.typeColorMap.default;
    },
    getUserType(userType) {
      return this.userTypeMap[userType] || this.userTypeMap.default;
    },
    formatJoinDate(dateString) {
      return moment(dateString).format("MMM DD, YYYY");
    },
    openPasswordReset() {
      this.passwordResetDialog = true;
    },
    handlePasswordResetSuccess(event) {
      this.$emit("password-reset-success", event);
    },
    handlePasswordResetError(event) {
      this.$emit("password-reset-error", event);
    },
    cancel() {
      Object.assign(this.selectedCustomer, this.original);
      this.$emit("setDialog", false);
      this.$emit("cancel");
    },
    async save() {
      if (this.$refs.form.validate()) {
        this.isSaving = true;

        try {
          this.$emit("setDialog", false);
          let index = this.customerData.indexOf(this.selectedCustomer);
          this.$store.commit("fetchCustomerBack/editing", index);

          const sendData = {
            user_country_tel: this.selectedCustomer.user_country_tel,
            user_email: this.selectedCustomer.user_email,
            user_facebook_id: this.selectedCustomer.user_facebook_id,
            user_first_name: this.selectedCustomer.user_first_name,
            user_id: this.selectedCustomer.user_id,
            user_last_name: this.selectedCustomer.user_last_name,
            user_role: this.selectedCustomer.user_role,
            user_sum_point: this.selectedCustomer.user_sum_point,
            user_tel: this.selectedCustomer.user_tel,
            user_type: this.selectedCustomer.user_type,
            user_verify: this.selectedCustomer.user_verify,
          };

          let checkSuccess = await this.$store.dispatch(
            "fetchCustomerBack/editCustomer",
            sendData
          );

          if (checkSuccess == "error") {
            Object.assign(this.selectedCustomer, this.original);
          }

          this.$emit("cancel");
        } catch (error) {
          console.error("Error saving customer:", error);
          Object.assign(this.selectedCustomer, this.original);
        } finally {
          this.isSaving = false;
        }
      }
    },
  },
};
</script>

<style scoped>
.edit-customer-card {
  border-radius: 16px;
  overflow: hidden;
}

.edit-header {
  display: flex;
  align-items: center;
  padding: 1.5rem 2rem;
  background: linear-gradient(135deg, #fddf00 0%, #f39c12 100%);
  gap: 1rem;
  position: relative;
}

.header-icon {
  background: rgba(0, 0, 0, 0.1);
  border-radius: 50%;
  padding: 0.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
}

.header-content {
  flex: 1;
}

.header-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: black;
  margin: 0;
}

.header-subtitle {
  font-size: 0.9rem;
  color: rgba(0, 0, 0, 0.8);
  margin: 0.25rem 0 0 0;
}

.header-badges {
  position: absolute;
  top: 1rem;
  right: 4rem;
}

.vip-badge {
  color: black !important;
  font-weight: 600;
}

.close-btn {
  background: rgba(0, 0, 0, 0.1) !important;
  color: black !important;
}

.edit-content {
  padding: 2rem;
}

.customer-summary {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1.5rem;
  background: rgba(253, 223, 0, 0.05);
  border-radius: 12px;
  border: 1px solid rgba(253, 223, 0, 0.2);
  margin-bottom: 2rem;
}

.customer-avatar {
  background: rgba(253, 223, 0, 0.2);
  border-radius: 50%;
  width: 56px;
  height: 56px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.customer-basic-info {
  flex: 1;
}

.customer-display-name {
  font-size: 1.3rem;
  font-weight: 600;
  color: #2c3e50;
  margin: 0 0 0.25rem 0;
}

.customer-id {
  font-size: 0.9rem;
  color: #6b7280;
  margin: 0 0 0.75rem 0;
  font-family: "Courier New", monospace;
}

.customer-stats {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.stat-chip {
  font-weight: 500;
}

.edit-form {
  margin-bottom: 1.5rem;
}

.form-section {
  margin-bottom: 1.5rem; /* Reduced spacing */
}

/* Remove styles for removed sections */
.info-grid,
.info-item,
.info-label,
.info-value {
  display: none;
}

.phone-group {
  display: grid;
  grid-template-columns: 140px 1fr;
  gap: 1rem;
  align-items: start;
}

.country-select {
  background: white;
  border-radius: 8px;
}

.phone-input {
  background: white;
  border-radius: 8px;
}

.action-notice {
  display: flex;
  gap: 0.75rem;
  padding: 1rem;
  background: rgba(59, 130, 246, 0.05);
  border: 1px solid rgba(59, 130, 246, 0.2);
  border-radius: 8px;
}

.notice-content {
  flex: 1;
}

.notice-title {
  font-size: 0.85rem;
  font-weight: 600;
  color: #1e40af;
  margin: 0 0 0.25rem 0;
}

.notice-text {
  font-size: 0.8rem;
  color: #4b5563;
  margin: 0;
  line-height: 1.4;
}

.edit-actions {
  padding: 1rem 2rem 1.5rem;
  background: #f8f9fa;
  border-top: 1px solid #e5e7eb;
}

.password-btn {
  font-weight: 600;
  text-transform: none;
  border-radius: 8px;
  color: white;
}

.cancel-btn {
  font-weight: 600;
  text-transform: none;
  border-radius: 8px;
  color: white;
}

.save-btn {
  font-weight: 600;
  text-transform: none;
  color: black !important;
  border-radius: 8px;
  min-width: 140px;
}

.save-btn:disabled {
  opacity: 0.6 !important;
}

/* Facebook chip styling */
.facebook-chip {
  color: white !important;
}

.facebook-chip .v-chip__content {
  color: white !important;
}

/* Mobile Responsiveness */
@media (max-width: 768px) {
  .edit-header {
    padding: 1rem 1.5rem;
    flex-wrap: wrap;
  }

  .header-badges {
    position: static;
    order: 1;
    width: 100%;
    margin-top: 0.5rem;
  }

  .edit-content {
    padding: 1.5rem;
  }

  .customer-summary {
    padding: 1rem;
  }

  .form-grid {
    grid-template-columns: 1fr;
  }

  .phone-group {
    grid-template-columns: 1fr;
  }

  .edit-actions {
    padding: 1rem 1.5rem;
    flex-direction: column;
    gap: 1rem;
  }

  .password-btn {
    width: 100%;
    order: 1;
  }

  .cancel-btn,
  .save-btn {
    width: 100%;
  }
}

@media (max-width: 480px) {
  .customer-summary {
    flex-direction: column;
    text-align: center;
  }

  .customer-stats {
    justify-content: center;
  }
}
</style>